'use client'
import { useEffect } from 'react'
import { datadogRum } from '@datadog/browser-rum'
import { datadogLogs } from '@datadog/browser-logs'
import { useAuth, useUser } from '@clerk/nextjs'
import packageJson from '../../../package.json'
import { redactTokenUrl, redactTokenUrlsInRumEvent } from '@/lib/redact/token-url'
import { SessionClaims } from '@/types/auth'
import { DATADOG_CLIENT_TOKEN, DATADOG_SITE } from '@/config/datadog-browser.mjs'

/**
 * First-party path both SDKs post their intake batches to, instead of
 * `browser-intake-datadoghq.com` directly. Locked-down K-12 school networks and
 * ad blockers drop the Datadog host, so the schools most likely to hit problems
 * were exactly the ones we went blind on. Same treatment PostHog gets via
 * `/sj-ph/*`.
 *
 * With this STRING form the SDK posts every batch to `<path>?ddforward=<encoded
 * intake path + query>`; `src/app/sj-dd/route.ts` validates `ddforward` against
 * an exact allowlist and forwards it. The value must stay in lockstep with that
 * route's location.
 */
const DATADOG_TELEMETRY_PROXY_PATH = '/sj-dd'

export let isDatadogInitialized = false

/**
 * Third-party noise dropped before it reaches Datadog.
 *
 * The RUM `beforeSend` and the browser-logs `beforeSend` each carried their own
 * byte-identical copy of this list. Extracted so the two cannot drift apart —
 * and so neither function trips the complexity gate.
 */
const IGNORED_MESSAGE_FRAGMENTS = [
  'Object Not Found Matching',
  'XHR error POST https://px.ads.linkedin.com',
  'XHR error POST https://events.launchdarkly.com/events/bulk',
  'network error (Error)',
  '[LaunchDarkly] network error',
  'Fetch error POST https://api.segment.io',
  'Error sending segment'
]

const isIgnoredMessage = (message: string) => IGNORED_MESSAGE_FRAGMENTS.some(fragment => message.includes(fragment))

// Hoisted out of the init effect: both SDKs read the same two values, and
// resolving them inline put six branches inside one function.
const DD_VERSION = packageJson?.version ?? 'unknown'
const DD_ENV = process.env.NODE_ENV ?? 'unknown'
const DD_CLIENT_TOKEN = DATADOG_CLIENT_TOKEN
const DD_SITE = DATADOG_SITE
const DD_SERVICE = 'sj-nextjs'

export const useDatadog = ({ user, sessionClaims }: { user?: any; sessionClaims?: SessionClaims } = {}) => {
  useEffect(() => {
    // Skip Datadog initialization in development
    if (process.env.NODE_ENV === 'development') {
      console.log('Datadog disabled in development mode')
      return
    }

    if (typeof window !== 'undefined' && !isDatadogInitialized && !datadogRum.getInitConfiguration()) {
      isDatadogInitialized = true
      // Initialize Datadog RUM
      datadogRum.init({
        version: DD_VERSION,
        applicationId: '9d995dc6-af4d-4023-ad20-b35a3f6acca8',
        clientToken: DD_CLIENT_TOKEN,
        site: DD_SITE,
        service: DD_SERVICE,
        env: DD_ENV,
        // Route intake through our own origin (see DATADOG_TELEMETRY_PROXY_PATH)
        // so locked-down school networks and ad blockers stop dropping it.
        proxy: DATADOG_TELEMETRY_PROXY_PATH,
        // Sampling is deliberately 100% for both sessions and replay while the
        // platform stabilises (Part B decision, 2026-07-20). Now that every
        // batch also costs a Vercel function invocation via the proxy, revisit
        // these two numbers together as traffic grows.
        sessionSampleRate: 100,
        sessionReplaySampleRate: 100,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'allow',
        beforeSend: event => {
          if (isIgnoredMessage((event.error as any)?.message || '')) return false
          // Token-bearing page and fetch URLs (SCH-3420): see `lib/redact/token-url.ts`.
          redactTokenUrlsInRumEvent(event)
          return true
        }
      })

      // Initialize Datadog Logs
      datadogLogs.init({
        version: DD_VERSION,
        clientToken: DD_CLIENT_TOKEN,
        site: DD_SITE,
        service: DD_SERVICE,
        env: DD_ENV,
        proxy: DATADOG_TELEMETRY_PROXY_PATH,
        forwardErrorsToLogs: true,
        sessionSampleRate: 100,
        beforeSend: logEvent => {
          if (isIgnoredMessage(logEvent.message || '')) return false
          // A log carries only the page it was written on.
          if (logEvent.view?.url) logEvent.view.url = redactTokenUrl(logEvent.view.url)
          if (logEvent.view?.referrer) logEvent.view.referrer = redactTokenUrl(logEvent.view.referrer)
          return true
        }
      })

      // Identity is NOT attached here — see `useDatadogUser` below for why.

      // Start session replay recording
      datadogRum.startSessionReplayRecording()
    }
  }, [user, sessionClaims])
}

/**
 * Attach the signed-in identity to RUM + browser-logs.
 *
 * Deliberately a SEPARATE hook from `useDatadog`, for two reasons:
 *
 * 1. `<DatadogInitializer />` is mounted OUTSIDE `<ClerkProvider>` in the root
 *    layout so RUM is already capturing before Clerk loads — we have a live
 *    "Failed to load Clerk" error class, and moving init inside the provider
 *    would blind us to exactly that. Clerk hooks cannot be called out there, so
 *    identity has to be attached from a second component mounted inside it.
 * 2. Identity arrives AFTER init. The old code set it inside the
 *    `!isDatadogInitialized` branch, so even once a caller passed claims the
 *    guard had already flipped and the branch never re-entered.
 *
 * Uses `datadogRum.setUser`, not `setGlobalContextProperty('user', …)`: only
 * `setUser` populates the `@usr.*` attributes that RUM search and the Datadog UI
 * read. The old call wrote to `@context.user.*` and — because `DatadogInitializer`
 * passed no arguments at all — never ran, so every RUM event to date carries
 * `usr: null`. That is why a production 403 storm could not be attributed to any
 * named user and had to be inferred from page paths.
 */
/** Prefer the JWT claim: it is the external user id, stable across schools. */
const resolveUserId = (claims: SessionClaims | null | undefined, user: any) => claims?.userId ?? user?.id

const resolveUserName = (claims: SessionClaims | null | undefined, user: any) =>
  claims?.name?.full || [user?.firstName, user?.lastName].filter(Boolean).join(' ') || undefined

const resolveUserEmail = (claims: SessionClaims | null | undefined, user: any) =>
  claims?.email || user?.primaryEmailAddress?.emailAddress || undefined

export const useDatadogUser = () => {
  const { sessionClaims } = useAuth()
  const { user } = useUser()

  useEffect(() => {
    // Never initialized (e.g. development, where init early-returns), so there is
    // nothing to attach to — the SDK would buffer these calls indefinitely.
    if (!isDatadogInitialized) return

    const claims = sessionClaims as SessionClaims | null | undefined
    const id = resolveUserId(claims, user)

    if (!id) {
      // Signed out (or not yet loaded) — drop any identity from a previous session
      // so events are not misattributed to the last user on a shared machine.
      datadogRum.clearUser()
      datadogLogs.clearUser()
      return
    }

    const identity = {
      id,
      name: resolveUserName(claims, user),
      email: resolveUserEmail(claims, user)
    }

    datadogRum.setUser(identity)
    datadogLogs.setUser(identity)
  }, [user, sessionClaims])
}

// For backward compatibility
export const useDatadogLoggedin = useDatadog

export default useDatadog
